import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSeo } from "../hooks/useSeo";
import { Reveal, StaggerGroup, staggerItem } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { REVIEWS_FEATURED } from "../data/content";
import { IMAGES } from "../config/site";

export default function Reviews() {
  useSeo({
    title: "Client Experiences | The Law Office of Ronald K. Nolen, LLC",
    description:
      "Verified client experiences with The Law Office of Ronald K. Nolen, LLC — grounded in preparation, communication, strategy, and protecting each client's interests.",
    path: "/reviews",
    image: IMAGES.buckingham,
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <>
      {/* Hero banner */}
      <section ref={heroRef} className="relative h-[62vh] min-h-[440px] w-full overflow-hidden" data-testid="reviews-hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
          <img
            src={IMAGES.buckingham}
            alt="Buckingham Fountain with the Chicago skyline on a bright day"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-navy/25 to-transparent" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-14 md:px-8 md:pb-20">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs font-600 uppercase tracking-label text-gold-soft">
            Testimonials
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-4 max-w-3xl font-serif text-5xl font-500 leading-[1.02] text-white sm:text-6xl">
            Client Experiences
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-5 max-w-2xl text-lg leading-relaxed text-white/85">
            The firm's work is grounded in preparation, communication, strategy, and a commitment to
            protecting each client's interests.
          </motion.p>
        </div>
      </section>

      {/* Review cards — verified Avvo reviews */}
      <section className="bg-warmwhite py-16 md:py-24" data-testid="reviews-grid">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <StaggerGroup className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REVIEWS_FEATURED.map((r, i) => (
              <motion.figure key={i} variants={staggerItem}
                data-testid={`review-card-${i}`}
                className="flex h-full flex-col rounded-sm border border-navy/10 bg-white p-8">
                <div className="flex items-start justify-between gap-4">
                  <span className="font-serif text-6xl leading-none text-gold">&ldquo;</span>
                  {r.label && (
                    <span className="mt-2 inline-block rounded-sm border border-gold/40 bg-cream px-2.5 py-1 text-[10px] font-600 uppercase tracking-wider text-navy">
                      {r.label}
                    </span>
                  )}
                </div>
                <h3 className="mt-1 font-serif text-xl font-500 leading-snug text-navy">
                  {r.title}
                </h3>
                <blockquote className="mt-4 flex-1 text-base font-400 leading-relaxed text-navy/85">
                  {r.quote}
                </blockquote>
                <figcaption className="mt-6 flex items-center justify-between border-t border-navy/10 pt-4 text-sm">
                  <span className="font-600 text-navy">— {r.name}</span>
                  <span className="text-bluegray-text">{r.date}</span>
                </figcaption>
              </motion.figure>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <CTASection
        eyebrow="Consultations"
        headline="Ready to Discuss Your Matter?"
        copy="Speak with a legal team prepared to understand your goals, evaluate your options, and help you take the next step."
        testid="reviews-cta"
      />
    </>
  );
}
